import { Box, LinearProgress, Typography, Chip } from '@mui/material';
import { useAppSelector } from "@store/hooks";
import { selectCurrentReading } from "@store/slices/workspaceSlice";

export function ReadingProgress() {
  const reading = useAppSelector(selectCurrentReading);

  // Only show for required readings
  if (!reading || !reading.required) return null;

  const hasHighlights = reading.annotation_count > 0;

  // The "Diagram" / "Paper" chips were a unified-model holdover (and were
  // dead — canvas/paper carry no reading_id, so the counts were always 0).
  // The annotate view now tracks just the reading's own highlight progress.
  const completionSteps = [
    {
      label: 'Highlights',
      completed: hasHighlights,
      count: reading.annotation_count,
    },
  ];

  const completedSteps = completionSteps.filter((s) => s.completed).length;
  const progressPercent = (completedSteps / completionSteps.length) * 100;

  const getStatusColor = (
    status: string
  ): 'default' | 'success' | 'warning' | 'error' | 'info' | 'primary' | 'secondary' => {
    switch (status) {
      case 'completed':
      case 'submitted':
        return 'success';
      case 'in_progress':
        return 'warning';
      case 'assigned':
        return 'default';
      default:
        return 'default';
    }
  };

  return (
    <Box sx={{ p: 2, bgcolor: 'background.default', borderRadius: 1, mb: 2 }}>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mb: 1,
        }}
      >
        <Typography variant="subtitle2">
          {reading.reading_name || 'Reading Progress'}
        </Typography>
        {reading.status && (
          <Chip
            size="small"
            label={reading.status.replace('_', ' ')}
            color={getStatusColor(reading.status)}
          />
        )}
      </Box>

      <LinearProgress
        variant="determinate"
        value={progressPercent}
        sx={{ mb: 1.5, height: 6, borderRadius: 3 }}
      />

      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
        {completionSteps.map((step) => (
          <Chip
            key={step.label}
            label={`${step.label}${step.count > 0 ? ` (${step.count})` : ''}`}
            size="small"
            color={step.completed ? 'primary' : 'default'}
            variant={step.completed ? 'filled' : 'outlined'}
          />
        ))}
      </Box>

      {reading.due_date && (
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ mt: 1, display: 'block' }}
        >
          Due: {new Date(reading.due_date).toLocaleDateString()}
        </Typography>
      )}

      {reading.description && (
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ mt: 1, display: 'block' }}
        >
          {reading.description}
        </Typography>
      )}
    </Box>
  );
}
